import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import { callFn, fmtDate, fmtDateTime } from '../contracts/contractsApi';
import StatusPill from './machine/StatusPill';
import MachineCarbonReadOnly from './machine/MachineCarbonReadOnly';
import {
  IconArrowLeft,
  IconTractor,
  IconClipboardList,
  IconTool,
  IconUsers,
  IconFileText,
  IconShare,
  IconAlertTriangle,
  IconAlertOctagon,
  IconLock,
  IconClock,
  IconBook,
  IconEye,
} from '@tabler/icons-react';

// Read-only mirror of Ops src/pages/machines/MachineProfile.js. Every write
// control from Ops is deliberately absent; data comes from getOpsMachine.

const TABS = [
  { key: 'overview',  label: 'Overview',  icon: IconTractor },
  { key: 'history',   label: 'History',   icon: IconClipboardList },
  { key: 'documents', label: 'Documents', icon: IconFileText },
  { key: 'operators', label: 'Operators', icon: IconUsers },
  { key: 'sharing',   label: 'Sharing',   icon: IconShare },
  { key: 'incidents', label: 'Incidents', icon: IconAlertTriangle },
  { key: 'faults',    label: 'Faults',    icon: IconAlertOctagon },
];

const num = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? null : Number(v));
const hrs = (v) => (num(v) ? `${num(v).toLocaleString()} hrs` : '—');
const money = (v) => `£${(num(v) || 0).toLocaleString()}`;

const AVATAR_COLOURS = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#EAF3DE', color: '#3B6D11' },
  { bg: '#EEEDFE', color: '#534AB7' },
  { bg: '#FCEBEB', color: '#A32D2D' },
  { bg: '#FDF0E8', color: '#993C1D' },
  { bg: '#E1F5EE', color: '#085041' },
];

function Avatar({ name }) {
  const c = name ? AVATAR_COLOURS[name.charCodeAt(0) % AVATAR_COLOURS.length] : AVATAR_COLOURS[0];
  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', background: c.bg, color: c.color,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function EmptyState({ icon, title, message }) {
  return (
    <div style={{ padding: '48px 32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {icon && <div style={{ marginBottom: 4, color: 'var(--color-text-tertiary)' }}>{icon}</div>}
      <div style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)' }}>{title}</div>
      {message && <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-tertiary)', maxWidth: 320, lineHeight: 1.6 }}>{message}</div>}
    </div>
  );
}

function DocumentCard({ title, typeLabel, Icon, doc }) {
  const hasFile = !!doc;
  return (
    <div style={{
      background: 'var(--color-bg-primary)',
      border: '0.5px solid var(--color-border-light)',
      borderRadius: 'var(--radius-lg)',
      padding: 16,
      minHeight: 180,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 'var(--radius-md)',
          background: hasFile ? 'rgba(232,97,10,0.08)' : 'var(--color-bg-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} stroke={1.5} color={hasFile ? 'var(--color-brand)' : 'var(--color-text-tertiary)'} />
        </div>
        <span style={{
          fontSize: 9, padding: '2px 8px', borderRadius: 'var(--radius-full)',
          background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{typeLabel}</span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, wordBreak: 'break-word' }}>
        {hasFile && doc.url ? (
          <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{title}</a>
        ) : title}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 12, flex: 1 }}>
        {hasFile ? (doc.uploadedAt ? fmtDate(doc.uploadedAt) : 'Uploaded') : 'No file yet'}
      </div>

      {hasFile && doc.url ? (
        <a href={doc.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--color-brand)', textDecoration: 'none' }}>
          View document →
        </a>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>—</div>
      )}
    </div>
  );
}

function DetailRows({ rows }) {
  return rows.map(row => (
    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
      <span style={{ fontSize: 13, color: 'var(--color-text-primary)', textAlign: 'right', fontFamily: row.mono ? 'var(--font-mono)' : 'inherit', textTransform: row.capitalise ? 'capitalize' : 'none' }}>
        {row.value || '—'}
      </span>
    </div>
  ));
}

const metaLabel = { fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 };
const panel = { background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)' };
const sectionTitle = { fontSize: 14, fontWeight: 400, marginBottom: 16 };

export default function OpsMachineProfile() {
  const { orgId, machineId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    callFn('getOpsMachine', { orgId, machineId })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e?.message || 'Could not load this machine.'); });
    return () => { cancelled = true; };
  }, [orgId, machineId]);

  const machine = data?.machine || null;
  const serviceRecords = useMemo(() => data?.serviceRecords || [], [data]);

  // Built exactly as Ops' costByMonth.
  const costByMonth = useMemo(() => {
    const buckets = {};
    serviceRecords.forEach(r => {
      if (!r.dateOfService) return;
      const month = r.dateOfService.slice(0, 7);
      if (!buckets[month]) buckets[month] = { service: 0, repair: 0 };
      const partsTotal = (r.parts || []).reduce((s, p) => s + ((Number(p.cost) || 0) * (Number(p.quantity) || 0)), 0);
      const total = (Number(r.cost) || 0) + partsTotal;
      buckets[month][r.type === 'repair' ? 'repair' : 'service'] += total;
    });
    const months = Object.keys(buckets).sort();
    let cumulative = Number(machine?.purchasePrice) || 0;
    return months.map(m => {
      cumulative += buckets[m].service + buckets[m].repair;
      return { month: m, service: buckets[m].service, repair: buckets[m].repair, cumulative };
    });
  }, [serviceRecords, machine?.purchasePrice]);

  const orgName = data?.org?.name;
  const backLink = (
    <div onClick={() => navigate(`/customers/ops/${orgId}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
      <IconArrowLeft size={15} stroke={1.5} /> {orgName ? `Back to ${orgName}` : 'Back to customer'}
    </div>
  );

  if (error) return (
    <Layout>
      <div style={{ padding: 28 }}>
        {backLink}
        <div style={{ ...panel, padding: '14px 16px', fontSize: 13, color: 'var(--color-danger)', background: 'var(--color-danger-light)', borderColor: 'var(--color-danger)' }}>
          {error}
        </div>
      </div>
    </Layout>
  );

  if (!data) return (
    <Layout>
      <div style={{ padding: 28 }}>
        {backLink}
        <Card>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ height: 14, width: `${90 - i * 8}%`, background: 'var(--color-bg-tertiary)', borderRadius: 4, margin: '10px 0' }} />
          ))}
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 8 }}>Loading...</div>
        </Card>
      </div>
    </Layout>
  );

  if (!machine) return (
    <Layout>
      <div style={{ padding: 28 }}>
        {backLink}
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Machine not found</div>
      </div>
    </Layout>
  );

  const { canonicalModel } = data;
  const documents = data.documents || [];
  const incidents = data.incidents || [];
  const checkRecords = data.checkRecords || [];
  const defects = data.defects || [];

  const heroUrl = canonicalModel?.heroImageUrl || machine.imageURL || machine.heroImageUrl;
  const isPool = !!(machine.isPool || machine.isPoolMachine);
  const blockedReason = machine.blockedReason || machine.blockedBy;
  const currentHours = num(machine.currentHours);
  const nextServiceHours = num(machine.nextServiceHours);
  const lastServiceHours = num(machine.lastServiceHours);
  const purchasePrice = num(machine.purchasePrice);
  const ct = machine.costTracking || {};
  const openDefects = defects.filter(d => d.status !== 'resolved').length;

  const serviceHoursProgress = nextServiceHours && lastServiceHours
    ? ((currentHours - lastServiceHours) / (nextServiceHours - lastServiceHours)) * 100
    : null;
  const progressColour = serviceHoursProgress > 80 ? 'var(--color-danger)' : serviceHoursProgress > 60 ? 'var(--color-warning)' : 'var(--color-success)';

  return (
    <Layout>
      <div style={{ padding: 28 }}>

        {backLink}

        {/* Read-only notice */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--color-info-light)', color: 'var(--color-info)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 12, marginBottom: 16 }}>
          <IconEye size={14} stroke={1.5} />
          Read-only view of {orgName || 'this customer'}'s Ops data
        </div>

        {/* Header */}
        <div style={{ ...panel, display: 'flex', gap: 20, padding: 20, marginBottom: 20 }}>
          <div style={{
            width: 260,
            minHeight: 160,
            alignSelf: 'stretch',
            flexShrink: 0,
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: machine.isBlocked ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)',
            border: machine.isBlocked ? '0.5px solid var(--color-danger)' : '0.5px solid var(--color-border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {heroUrl ? (
              <img src={heroUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: machine.isBlocked ? 0.4 : 1 }} />
            ) : machine.isBlocked ? (
              <IconLock size={48} color="var(--color-danger)" stroke={1.5} />
            ) : (
              <IconTractor size={48} color="var(--color-text-tertiary)" stroke={1} />
            )}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
                  {machine.makeLabel || machine.make || 'Unknown'} {machine.modelLabel || machine.model || ''}
                </h1>
                <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} size="lg" />
              </div>
              {machine.category && (
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>
                  {machine.category}{machine.subCategory ? ` · ${machine.subCategory}` : ''}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginTop: 'auto', paddingTop: 12 }}>
              {machine.registration && (
                <div>
                  <div style={metaLabel}>Registration</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, background: '#FAC775', color: '#412402', padding: '2px 8px', borderRadius: 3, display: 'inline-block' }}>
                    {machine.registration}
                  </div>
                </div>
              )}
              {machine.serialNumber && (
                <div>
                  <div style={metaLabel}>Serial number</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{machine.serialNumber}</div>
                </div>
              )}
              {machine.year && (
                <div>
                  <div style={metaLabel}>Year</div>
                  <div style={{ fontSize: 13 }}>{machine.year}</div>
                </div>
              )}
              {machine.siteName && (
                <div>
                  <div style={metaLabel}>Site</div>
                  <div style={{ fontSize: 13 }}>{machine.siteName}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Blocked banner */}
        {machine.isBlocked && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={18} color="var(--color-danger)" stroke={1.5} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-danger)' }}>Machine blocked — do not use</div>
              {blockedReason && (
                <div style={{ fontSize: 12, color: 'var(--color-danger)', opacity: 0.8, marginTop: 2 }}>{blockedReason}</div>
              )}
            </div>
          </div>
        )}

        {/* Quick stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Current hours', value: hrs(currentHours), colour: 'var(--color-text-primary)' },
            { label: 'Next service', value: hrs(nextServiceHours), colour: progressColour },
            { label: 'Service records', value: serviceRecords.length, colour: 'var(--color-text-primary)' },
            { label: 'Incidents', value: incidents.length, colour: incidents.length > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' },
          ].map(s => (
            <div key={s.label} style={{ ...panel, padding: '14px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Service progress bar */}
        {serviceHoursProgress !== null && (
          <div style={{ ...panel, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Service progress</span>
              <span style={{ fontSize: 12, color: serviceHoursProgress > 80 ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                {currentHours?.toLocaleString() ?? '—'} / {nextServiceHours?.toLocaleString()} hrs
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--color-bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.max(0, Math.min(serviceHoursProgress, 100))}%`,
                background: progressColour,
                borderRadius: 3,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )}

        {/* Cost chart */}
        <div style={{ ...panel, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Cost over time</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
              Service, repair and cumulative cost of ownership
            </div>
          </div>
          {costByMonth.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              Cost data will appear as service records are added.
            </div>
          ) : (
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={costByMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" />
                  <XAxis dataKey="month" stroke="var(--color-text-tertiary)" fontSize={11} />
                  <YAxis stroke="var(--color-text-tertiary)" fontSize={11} tickFormatter={v => `£${v}`} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-bg-primary)',
                      border: '0.5px solid var(--color-border-light)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 12,
                    }}
                    formatter={v => `£${Number(v).toFixed(2)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="service" stroke="#E8610A" strokeWidth={2} name="Service" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="repair" stroke="#DC2626" strokeWidth={2} name="Repair" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cumulative" stroke="#6B7280" strokeWidth={2} strokeDasharray="4 4" name="Cumulative" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, 1fr)`, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 14px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {tab.label}
                {tab.key === 'faults' && openDefects > 0 && (
                  <span style={{ fontSize: 10, padding: '0 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-danger-light)', color: 'var(--color-danger)' }}>{openDefects}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <MachineCarbonReadOnly machine={machine} checks={checkRecords} />
            </div>
            <Card>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Machine details</div>
              <DetailRows rows={[
                { label: 'Make', value: machine.makeLabel || machine.make },
                { label: 'Model', value: machine.modelLabel || machine.model },
                { label: 'Category', value: machine.category, capitalise: true },
                { label: 'Year', value: machine.year },
                { label: 'Serial number', value: machine.serialNumber, mono: true },
                { label: 'Registration', value: machine.registration, mono: true },
                { label: 'Current hours', value: hrs(currentHours) },
                { label: 'Site', value: machine.siteName },
                { label: 'Assigned to', value: isPool ? 'Pool machine' : (machine.assignedOperatorName || 'Unassigned') },
              ]} />
            </Card>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Card>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Purchase & value</div>
                <DetailRows rows={[
                  { label: 'Purchase date', value: machine.purchaseDate ? fmtDate(machine.purchaseDate) : '—' },
                  { label: 'Purchase price', value: purchasePrice ? `£${purchasePrice.toLocaleString()}` : '—' },
                  { label: 'Depreciation rate', value: machine.depreciationRate ? `${machine.depreciationRate}% per year` : '—' },
                  { label: 'Estimated life', value: machine.estimatedLifeYears ? `${machine.estimatedLifeYears} years` : '—' },
                ]} />
              </Card>

              <Card>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Cost tracking</div>
                <DetailRows rows={[
                  { label: 'Total service cost', value: money(ct.totalServiceCost) },
                  { label: 'Total repair cost', value: money(ct.totalRepairCost) },
                  { label: 'Total fuel cost', value: money(ct.totalFuelCost) },
                ]} />
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>Total cost of ownership</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {money((num(ct.totalServiceCost) || 0) + (num(ct.totalRepairCost) || 0) + (num(ct.totalFuelCost) || 0) + (purchasePrice || 0))}
                  </span>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* History */}
        {activeTab === 'history' && (
          <div>
            <div style={sectionTitle}>Service & repair history</div>
            {serviceRecords.length === 0 ? (
              <Card>
                <EmptyState icon={<IconTool size={28} stroke={1.5} />} title="No service records yet" message="No service or repair records have been added for this machine." />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {serviceRecords.map(record => (
                  <Card key={record.id}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)', textTransform: 'capitalize' }}>{record.type}</span>
                          {record.sealed && (
                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#E6F1FB', color: '#185FA5' }}>Sealed</span>
                          )}
                          {record.source === 'workshop' && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '1px 6px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                              <IconLock size={9} stroke={2} />
                              {record.workshopName || 'Workshop'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
                          {record.description || '—'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                            <IconClock size={11} stroke={1.5} style={{ verticalAlign: -1, marginRight: 3 }} />
                            {fmtDate(record.dateOfService)}
                          </span>
                          {num(record.hoursAtService) ? (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{hrs(record.hoursAtService)}</span>
                          ) : null}
                          {record.performedBy && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{record.performedBy}</span>
                          )}
                          {record.workshopJobRef && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontFamily: 'var(--font-mono, monospace)' }}>{record.workshopJobRef}</span>
                          )}
                          {Array.isArray(record.media) && record.media.length > 0 && (
                            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                              {record.media.length} {record.media.length === 1 ? 'photo' : 'photos'}
                            </span>
                          )}
                        </div>
                      </div>
                      {num(record.cost) ? (
                        <div style={{ fontSize: 14, color: 'var(--color-text-primary)', flexShrink: 0, marginLeft: 12 }}>
                          £{num(record.cost).toLocaleString()}
                        </div>
                      ) : null}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 16, lineHeight: 1.6 }}>
              Operator and workshop manuals are typed slots. Other documents are private to {orgName || 'the customer'}.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              <DocumentCard title="Operator's manual" typeLabel="Manual" Icon={IconBook} doc={documents.find(d => d.type === 'operators_manual')} />
              <DocumentCard title="Workshop manual" typeLabel="Manual" Icon={IconTool} doc={documents.find(d => d.type === 'workshop_manual')} />
              {documents
                .filter(d => d.type !== 'operators_manual' && d.type !== 'workshop_manual')
                .map(d => (
                  <DocumentCard key={d.id} title={d.name || 'Untitled document'} typeLabel="Private" Icon={IconFileText} doc={d} />
                ))}
            </div>
          </div>
        )}

        {/* Operators */}
        {activeTab === 'operators' && (
          <div>
            <div style={sectionTitle}>Assigned operators</div>
            {isPool ? (
              <Card>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', textAlign: 'center', padding: '16px 0' }}>
                  This is a pool machine — available to all operators
                </div>
              </Card>
            ) : !machine.assignedOperatorName ? (
              <Card>
                <EmptyState icon={<IconUsers size={28} stroke={1.5} />} title="No operator assigned" message="This machine has no assigned operator" />
              </Card>
            ) : (
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                  <Avatar name={machine.assignedOperatorName} />
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--color-text-primary)' }}>{machine.assignedOperatorName}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                      Operator{machine.siteName ? ` · ${machine.siteName}` : ''}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Sharing */}
        {activeTab === 'sharing' && (
          <div>
            <div style={sectionTitle}>Ownership & sharing</div>
            <Card>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10, fontWeight: 400 }}>Current owner</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 16 }}>
                {!machine.ownedBy || machine.ownedBy === orgId ? (orgName || orgId) : (
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{machine.ownedBy}</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 10, fontWeight: 400 }}>Shared with</div>
              {!machine.sharedWith?.length ? (
                <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Not shared with any other organisations</div>
              ) : machine.sharedWith.map((id, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--color-text-primary)', fontFamily: 'var(--font-mono)', padding: '6px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  {typeof id === 'string' ? id : JSON.stringify(id)}
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Incidents */}
        {activeTab === 'incidents' && (
          <div>
            <div style={sectionTitle}>Incident history</div>
            {incidents.length === 0 ? (
              <Card>
                <EmptyState icon={<IconAlertTriangle size={28} stroke={1.5} />} title="No incidents" message="No incidents recorded for this machine" />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.map(inc => (
                  <Card key={inc.id}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 4 }}>
                          {inc.triggerType === 'impact' ? 'Impact detected' : inc.triggerType === 'manual' ? 'Manual SOS' : 'Man-down alert'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                          {inc.triggeredByName || 'Unknown'} · {fmtDateTime(inc.detectedAt)}
                        </div>
                      </div>
                      <StatusPill status={inc.status || 'open'} />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Faults (HQ-only: defects on this machine) */}
        {activeTab === 'faults' && (
          <div>
            <div style={sectionTitle}>
              Faults
              <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginLeft: 8 }}>
                {openDefects} open of {defects.length}{defects.length >= 50 ? ' (latest 50)' : ''}
              </span>
            </div>
            {defects.length === 0 ? (
              <Card>
                <EmptyState icon={<IconAlertOctagon size={28} stroke={1.5} />} title="No faults" message="No defects have been reported on this machine" />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {defects.map(d => (
                  <Card key={d.id}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <StatusPill status={d.severity || 'minor'} />
                          <StatusPill status={d.status || 'open'} />
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                          {d.description || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                          Reported by {d.reportedBy || 'Unknown'} · {fmtDateTime(d.reportedAt)}
                        </div>
                        {(d.resolvedAt || d.resolution) && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: d.resolution ? 3 : 0 }}>
                              Resolved {d.resolvedAt ? fmtDateTime(d.resolvedAt) : ''}
                            </div>
                            {d.resolution && (
                              <div style={{ fontSize: 12, color: 'var(--color-text-primary)', whiteSpace: 'pre-wrap' }}>{d.resolution}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}
